import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PositionsList from './PositionsList/PositionsList';
import PositionsSkeleton from './PositionsSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from '../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './Positions.scss';

const Positions = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { data: mainData, loading, error, refetch } = useCachedFetch('/data/locales/mainData.json');

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const positionsData = useMemo(() => {
        return mainData?.TopMainData?.CareersSection;
    }, [mainData]);

    const isDataValid = useMemo(() => {
        if (!positionsData) return false;

        const requiredFields = [
            'contentAccent',
            'contentTitle', 
            'contentSubtitle',
            'positionsTitle'
        ];

        return requiredFields.every(field => {
            const fieldData = positionsData[field];
            return fieldData && fieldData[currentLang] !== undefined;
        });
    }, [positionsData, currentLang]);

    useEffect(() => {
        if (loading) return;

        if (error) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        if (isDataValid) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [loading, error, isDataValid]);

    if (isLoading) {
        return <PositionsSkeleton />;
    }

    if (hasError || !positionsData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className='careers__positions__section'>
            <div className='left__side__positions__section'>
                <div className='positions__content'>
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                    <p className='positions__accent'>
                        {positionsData.contentAccent?.[currentLang] || positionsData.contentAccent?.en || ''}
                    </p>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={100}>
                    <h4 className='positions__title'>
                        {positionsData.contentTitle?.[currentLang] || positionsData.contentTitle?.en || ''}
                    </h4>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={200}>
                    <h5 className='positions__subtitle'>
                        {positionsData.contentSubtitle?.[currentLang] || positionsData.contentSubtitle?.en || ''}
                    </h5>
                    </AnimatedOnScroll>
                </div>
            </div>
            <div className='right__side__positions__section'>
                <h4 className='positions__accent'>
                    {positionsData.positionsTitle?.[currentLang] || positionsData.positionsTitle?.en || ''}
                </h4>
                <PositionsList parentIsLoading={isLoading} />
            </div>
        </div>
    );
};

export default React.memo(Positions);